import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { Router } from '@angular/router';

// Mutex para evitar múltiples refreshes concurrentes cuando varios requests reciben 401 al mismo tiempo
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const AuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

  const router = inject(Router);
  const authService = inject(AuthService);
  const token = authService.getToken();


  if (token) {
    request = request.clone({
      setHeaders: { Authorization: token }
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {

      if (error.status === 401) {
        return handle401Error(request, next, authService, router);
      }

      if (error.status === 419) {
        console.warn(`[AuthInterceptor] 419 recibido en ${request.url} | isRefreshing=${isRefreshing} | refreshTokenSubject.value=${refreshTokenSubject.value ? refreshTokenSubject.value.slice(-12) : 'null'}`);
        
        clearSessionAndRedirect(authService, router);
        // EMPTY completa el observable sin emitir error,
        // evitando que el componente reaccione y dispare otra petición
        return EMPTY;
      }

      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {

  const user = authService.getUser();

  if (!user?.email) {
    clearSessionAndRedirect(authService, router);
    return EMPTY;
  }

  // Si ya hay un refresh en curso, encolar este request hasta que el nuevo token llegue
  if (isRefreshing) {
    console.warn(`[AuthInterceptor] ⏳ Request encolado (isRefreshing=true) para ${request.url} | refreshTokenSubject.value=${refreshTokenSubject.value ? refreshTokenSubject.value.slice(-12) : 'null'}`);
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(newToken => {
        return next(cloneWithToken(request, newToken!));
      })
    );
  }

  isRefreshing = true;
  refreshTokenSubject.next(null);

  return from(authService.refreshToken(user.email)).pipe(
    switchMap((response: any) => {
      const newToken: string = response.data.token;
      isRefreshing = false;
      localStorage.setItem('accessToken', newToken);
      refreshTokenSubject.next(newToken);
      return next(cloneWithToken(request, newToken));
    }),
    catchError(() => {
      // El refresh token también falló: cerrar sesión de forma limpia
      isRefreshing = false;
      refreshTokenSubject.next(null);
      clearSessionAndRedirect(authService, router);
      return EMPTY;
    })
  );
}

function cloneWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({ setHeaders: { Authorization: token } });
}

function clearSessionAndRedirect(authService: AuthService, router: Router): void {
  localStorage.removeItem('accessToken');
  authService.setUser(null);
  const returnUrl = router.url.startsWith('/clubes-juveniles/inscripcion') ? router.url : null;
  router.navigate(['iniciar-sesion'], { queryParams: returnUrl ? { returnUrl } : {} });
}
